/**
 * The tunables the *pilots* run on, split out of `tunables.ts` so the register
 * for how an aeroplane flies stays separate from the register for how the
 * people in them behave. They are merged straight back into `DEFAULT_TUNABLES`,
 * so nothing downstream sees two tables — `Tunables` is still one flat record
 * and `makeTunables` still overrides any key.
 *
 * Every value here is CALIBRATION-PENDING in the sense flight-tunables.md §8
 * uses: no file in the game data carries an AI decision interval or a firing
 * cone, so these are shaped-right starting points measured against the census
 * harness (docs/engineering/lessons.md, combat lane), not transcribed figures.
 */

export const AI_TUNABLES = {
  /** Seconds between AI manoeuvre decisions at the reference skill. CALIBRATION-PENDING. */
  aiDecisionIntervalSeconds: 0.8,
  /** Half-angle the target must be inside before the AI squeezes the trigger, at the reference skill. CALIBRATION-PENDING. */
  aiFiringConeRad: 0.12,
  /** The AI will not fire closer than this — collision avoidance, and the manual's own "closing to within 30 yards" is the *good* pilot's limit. CALIBRATION-PENDING. */
  aiMinFiringRangeM: 25,
  /** The AI will not open fire beyond this, at the reference skill. CALIBRATION-PENDING. */
  aiMaxFiringRangeM: 260,
  /**
   * Angular error in the AI's own firing solution at the reference skill, scaled
   * per tier by `aimErrorScale`. This is where pilot quality lives now that
   * rounds fly dead straight and hits are pure geometry: a worse pilot points
   * the aeroplane in the wrong place, rather than owning a gun that sprays.
   * Resampled once per manoeuvre decision, so it reads as a settled aiming
   * mistake rather than as tick-rate noise. CALIBRATION-PENDING — measured
   * against the duel census, since no source gives an AI accuracy figure.
   *
   * 0.024 rad (1.37° at the reference hand, 4.7° for a novice) is the value a
   * 120-duel sweep picked: it is the only candidate in 0.012…0.030 that keeps
   * the skill ladder monotone while landing the fleet hit rate (5.60 %) and the
   * mean decided time (62.2 s) on the figures the cone model shipped with
   * (5.23 %, 64.3 s) — the fights are as decisive as they were and the AI is as
   * accurate as it was, and all that changed is *how* it misses.
   */
  aiAimErrorRad: 0.024,
  /** Burst length the AI holds the trigger for, at the reference skill — the manual's "fire short bursts". CALIBRATION-PENDING. */
  aiBurstSeconds: 0.7,
  /** Gap between AI bursts. CALIBRATION-PENDING. */
  aiBurstGapSeconds: 1.1,
  /** Range inside which an enemy astern counts as a threat worth evading. CALIBRATION-PENDING. */
  aiThreatRangeM: 480,
  /** Half-angle behind the AI within which an enemy counts as being on its tail. CALIBRATION-PENDING. */
  aiThreatConeRad: 0.9,
  /** Worst component health below which the AI starts flying to survive rather than to kill. CALIBRATION-PENDING. */
  aiDamageBreakOffHealth: 0.45,
  /** Altitude below which the AI abandons its manoeuvre and pulls out. CALIBRATION-PENDING. */
  aiGroundClearanceM: 320,
  /** Proportional gain from a bank error to aileron. CALIBRATION-PENDING. */
  aiRollGain: 2.2,
  /** Rate damping on the roll channel, so the AI does not oscillate about a bank. CALIBRATION-PENDING. */
  aiRollDamping: 0.55,
  /** Proportional gain from an angle-of-attack error to elevator. CALIBRATION-PENDING. */
  aiPitchGain: 9,
  /** Rudder gain against sideslip — the AI's own coordination. CALIBRATION-PENDING. */
  aiRudderGain: 2.5,
  /** Fraction of the stall angle the AI pulls to in a hard turn. CALIBRATION-PENDING. */
  aiPullAlphaFraction: 0.85,
  /** Range at which a flight following a route abandons it and joins the fight. Wider than `aiThreatRangeM` on purpose: an escort breaks off at the sighting, not at the merge. CALIBRATION-PENDING. */
  aiEngageRangeM: 900,
  /** Range inside which a flight that broke off a route counts the fight as over and resumes it. Hysteresis against a flight that oscillates between the two. CALIBRATION-PENDING. */
  aiDisengageRangeM: 1600,
  /** How close a waypoint must be before a route flight calls it reached. CALIBRATION-PENDING. */
  routeCaptureRadiusM: 400,
  /** Metres of altitude error a route flight will trade for one radian of commanded pitch. CALIBRATION-PENDING. */
  routeAltitudeToPitchM: 900,
} satisfies Record<string, number>;

export type AiTunableKey = keyof typeof AI_TUNABLES;

/** Every key above, so `CALIBRATION_PENDING_TUNABLES` stays total without a second hand-written list. */
export const AI_TUNABLE_KEYS: readonly AiTunableKey[] = Object.keys(AI_TUNABLES).filter(
  (key): key is AiTunableKey => key in AI_TUNABLES,
);
